import React from "react"
import Modal, { ModalBaseProps } from "@components/Modal"
import DesktopReviewsModal from "./DesktopReviewsModal"
import MobileReviewsModal from "./MobileReviewsModal"
import { YotpoReview } from "../../../reviews/types"
import { useProductReviews } from "../../../reviews/useProductReviews"
import { DesktopOnly, MobileAndTabletOnly } from "@utils/MediaQuery"
import _ from "lodash-es"

interface ReviewsModalProps extends ModalBaseProps {
  productId: string
  reviews: YotpoReview[]
  avgRating: number
  numReviews: number
  onClickWriteReview: () => void
  onRequestOpenReviewImageGallery: any
}

const ReviewsModal = (props: ReviewsModalProps) => {
  const { productId, avgRating, numReviews, ...rest } = props
  const { reviews, onScrollFetchMore } = useProductReviews(productId)
  const [overlayRef, setOverlayRef] = React.useState<HTMLDivElement | null>(
    null
  )

  const onScrollListener = _.throttle(
    () => {
      if (overlayRef) {
        /*
         ** Threshold is 30% of the screen height
         */
        const threshold =
          (overlayRef?.scrollHeight ?? 0) -
          (overlayRef?.clientHeight ?? 0) -
          window.innerHeight * 0.3

        /*
         ** Only get more reviews if the threshold was reached,
         ** there are no pending requests,
         ** and if there are still more reviews to be fetched
         */
        if ((overlayRef?.scrollTop ?? 0) >= threshold) {
          onScrollFetchMore()
        }
      }
    },
    700,
    { leading: true }
  )

  React.useEffect(() => {
    overlayRef?.addEventListener("scroll", onScrollListener)

    /*
     ** Fetch more reviews if there is not enough reviews to
     ** fill the entire height of screen.
     */
    if (
      (overlayRef?.scrollHeight ?? 0) - (overlayRef?.clientHeight ?? 0) ===
      0
    ) {
      onScrollFetchMore()
    }

    return () => {
      overlayRef?.removeEventListener("scroll", onScrollListener)
    }
  }, [overlayRef])

  const reviewImages = React.useMemo(() => {
    return _.flatten(
      _.map(reviews, (review) => {
        return review.images_data
          ? _.map(review.images_data, (data) => {
              return {
                id: data.id,
                review: review.id,
                alt: review.title,
                image: data.original_url,
                thumbnail: data.thumb_url,
                onClick: () => {
                  props.onRequestOpenReviewImageGallery(data.id)
                },
              }
            })
          : []
      })
    ).filter((image) => !!image)
  }, [reviews])

  const onClickCount = () => {
    if (reviewImages.length > 0 && props.onRequestOpenReviewImageGallery) {
      props.onRequestOpenReviewImageGallery(reviewImages[0].id)
    }
  }

  return (
    <Modal overlayRef={(ref) => setOverlayRef(ref)} {...rest}>
      <DesktopOnly>
        <DesktopReviewsModal
          {...props}
          reviews={reviews}
          reviewImages={reviewImages}
          onClickImage={props.onRequestOpenReviewImageGallery}
          onClickCount={onClickCount}
        />
      </DesktopOnly>
      <MobileAndTabletOnly>
        <MobileReviewsModal
          {...props}
          reviews={reviews}
          onClickImage={props.onRequestOpenReviewImageGallery}
        />
      </MobileAndTabletOnly>
    </Modal>
  )
}

export default ReviewsModal
